import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiInfo, FiBell, FiCheck, FiAlertTriangle, FiSlash, FiX } from 'react-icons/fi';
import './Toast.css';

/**
 * -------------------------------------------------------------
 * USO GLOBAL DEL TOAST (ToastManager)
 * -------------------------------------------------------------
 * Para utilizar el toast desde cualquier parte de la aplicación,
 * simplemente importa la función `toast` y llámala con un objeto
 * de configuración.
 *
 * Ejemplo de uso:
 *
 * import { toast } from '../widgets/alerts/Toast';
 *
 * toast({
 *   title: 'Operación Exitosa',
 *   description: 'El documento se ha guardado.',
 *   type: 'successful',    // 'neutral' | 'neutral-blue' | 'successful' | 'warning' | 'error'
 *   position: 'bottom-center', // 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center'
 *   duration: 5            // Tiempo en segundos. Usa 0 o Infinity para que no se cierre automáticamente
 * });
 * -------------------------------------------------------------
 */

const icons = {
  neutral: <FiInfo />,
  'neutral-blue': <FiBell />,
  successful: <FiCheck style={{ strokeWidth: 3 }} />,
  warning: <FiAlertTriangle />,
  error: <FiSlash />
};

let toastContainerElement = null;
let addToastFunction = null;
let removeToastFunction = null;
const toastsCancelados = new Set(); // ids cerrados con dismissToast antes de llegar a mostrarse
const nuevoId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

// ---- Constantes de física del stack ----
const PEEK = 40;  // px que asoma el toast de detrás
const GAP = 25;  // px de separación entre toasts desplegados
const EST_H = 82;  // altura de fallback hasta que se mida el toast real
const MAX_VIS = 3;   // máximo toasts visibles en el stack

const isBottomPos = (pos) => pos && pos.startsWith('bottom');

// ==========================================
// 1. COMPONENTE INDIVIDUAL DE TOAST
// ==========================================
const ToastMessage = React.memo(function ToastMessage({
  id, type = 'neutral', title, description, duration = 4,
  onClose, onHeightChange,
  yOffset,
  isBottom,
  isExpanded,
  index,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const rafRef = useRef(null);
  const domRef = useRef(null);

  useEffect(() => {
    if (!domRef.current || !window.ResizeObserver) return;
    const ro = new window.ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (domRef.current) {
          const h = domRef.current.getBoundingClientRect().height;
          if (h) onHeightChange(id, h);
        }
      });
    });
    ro.observe(domRef.current);
    return () => ro.disconnect();
  }, [id, onHeightChange]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => setIsVisible(true));
    });
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => onClose(id), 450);
  }, [id, onClose]);

  useEffect(() => {
    if (duration > 0 && duration !== Infinity) {
      const timer = setTimeout(() => handleClose(), duration * 1000);
      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  if (!isExpanded && index >= MAX_VIS) return null;

  const sign = isBottom ? -1 : 1;

  const entryShift = isVisible && !isClosing ? 0 : sign * 20;
  const finalY = sign * yOffset + entryShift;

  const finalOpacity = isVisible && !isClosing
    ? (isExpanded ? 1 : Math.max(1 - index * 0.12, 0.65))
    : 0;
  const finalScale = isVisible && !isClosing
    ? (isExpanded ? 1 : 1 - index * 0.05)
    : 0.9;

  return (
    <div
      ref={domRef}
      className={`tw-toast tw-toast-${type}`}
      style={{
        transform: `scale(${finalScale}) translateY(${finalY}px)`,
        opacity: finalOpacity,
        zIndex: MAX_VIS - index,
      }}
    >
      <div className={`tw-toast-icon tw-toast-icon-${type}`}>{icons[type]}</div>
      <div className="tw-toast-content">
        <div className="tw-toast-title">{title}</div>
        {description && <div className="tw-toast-desc">{description}</div>}
      </div>
      <button className="tw-toast-close" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
        <FiX />
      </button>
    </div>
  );
});

// ==========================================
// 2. GESTOR GLOBAL DE TOASTS
// ==========================================
const ToastManager = () => {
  const [toasts, setToasts] = useState([]);
  const [expandedPositions, setExpandedPositions] = useState({});
  const [heights, setHeights] = useState({});

  useEffect(() => {
    addToastFunction = (opts) => {
      const id = opts.id || nuevoId();
      if (toastsCancelados.has(id)) { toastsCancelados.delete(id); return; }
      setToasts(prev => [{ ...opts, id }, ...prev]);
    };
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    setHeights(prev => { const n = { ...prev }; delete n[id]; return n; });
  }, []);

  useEffect(() => {
    removeToastFunction = removeToast;
  }, [removeToast]);

  const handleHeightChange = useCallback((id, h) => {
    setHeights(prev => prev[id] === h ? prev : { ...prev, [id]: h });
  }, []);

  const setExpanded = useCallback((pos, val) =>
    setExpandedPositions(prev => ({ ...prev, [pos]: val })), []);

  const getByPos = (pos) => toasts.filter(t => (t.position || 'top-right') === pos);

  const computeOffsets = useCallback((posToasts, isExpanded) => {
    const visible = posToasts.slice(0, MAX_VIS);
    const offsets = [0];
    for (let i = 1; i < visible.length; i++) {
      const hPrev = heights[visible[i - 1].id] || EST_H;
      const hCurr = heights[visible[i].id] || EST_H;
      if (isExpanded) {
        offsets[i] = offsets[i - 1] + hPrev + GAP;
      } else {
        offsets[i] = offsets[i - 1] + hPrev + PEEK - hCurr;
      }
    }
    return offsets;
  }, [heights]);

  const computeContainerHeight = useCallback((posToasts, isExpanded) => {
    const visible = posToasts.slice(0, MAX_VIS);
    const offsets = computeOffsets(posToasts, isExpanded);
    const lastI = visible.length - 1;
    const hLast = (visible[lastI] && heights[visible[lastI].id]) || EST_H;
    return Math.max(offsets[lastI] + hLast, EST_H);
  }, [computeOffsets, heights]);

  const positions = ['top-right', 'top-left', 'top-center', 'bottom-right', 'bottom-left', 'bottom-center'];

  return (
    <React.Fragment>
      {positions.map(position => {
        const posToasts = getByPos(position);
        if (posToasts.length === 0) return null;

        const isExpanded = expandedPositions[position] || false;
        const isBottom = isBottomPos(position);
        const offsets = computeOffsets(posToasts, isExpanded);
        const containerHeight = computeContainerHeight(posToasts, isExpanded);

        return (
          <div
            key={position}
            className={`tw-toast-container tw-toast-container-${position} ${isExpanded ? 'tw-toast-container-expanded' : ''}`}
            style={{ height: containerHeight + 'px' }}
            onMouseEnter={() => setExpanded(position, true)}
            onMouseLeave={() => setExpanded(position, false)}
          >
            {posToasts.map((t, index) => (
              <ToastMessage
                key={t.id}
                {...t}
                index={index}
                yOffset={offsets[index] || 0}
                isExpanded={isExpanded}
                isBottom={isBottom}
                onClose={removeToast}
                onHeightChange={handleHeightChange}
              />
            ))}
          </div>
        );
      })}
    </React.Fragment>
  );
};

// ==========================================
// 3. FUNCIÓN IMPERATIVA EXPORTADA
// ==========================================
// Devuelve el id del toast para poder cerrarlo con dismissToast (p. ej. un aviso persistente con duration: 0).
export const toast = (options) => {
  const id = options.id || nuevoId();
  const opts = { ...options, id };
  if (!toastContainerElement) {
    toastContainerElement = document.createElement('div');
    toastContainerElement.id = 'tw-toast-root';
    document.body.appendChild(toastContainerElement);
    ReactDOM.render(<ToastManager />, toastContainerElement);
  }
  if (addToastFunction) {
    addToastFunction(opts);
  } else {
    setTimeout(() => { if (addToastFunction) addToastFunction(opts); }, 50);
  }
  return id;
};

// Cierra un toast por id (también si aún no llegó a mostrarse).
export const dismissToast = (id) => {
  if (!id) return;
  toastsCancelados.add(id);
  setTimeout(() => toastsCancelados.delete(id), 1000);
  if (removeToastFunction) removeToastFunction(id);
};



// WEBPACK FOOTER //
// ./src/components/widgets/alerts/Toast.jsx